import React from 'react';
import { ShieldOff } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import GenericButton from '@ui-components/Button/GenericButton';
import {
  LICENSING_PATH,
  buildLicenseExpiredBody,
  buildLicenseGateTitle
} from '../../helpers/bgpValidation';

interface LicenseExpiredOverlayProps {
  /** How to name the target, e.g. "CTXR-5cb248" or "Primary CTXR-5cb248" in a cluster. */
  subject: string;
  /** Validity reason from the license-validity endpoint — drives which sentence is shown. */
  reason: string;
  /** The license's expiry date, when there is one. */
  endDate?: string | null;
}

/**
 * Centered "license is not valid" card for a gated view (CTX-346), generalising the
 * reflector-down overlay of CTX-125: the view behind stays drawn but blurred and inert, so it
 * reads as the last known state rather than live, actionable data.
 *
 * The scrim swallows pointer events, which is what makes the content behind read-only; only the
 * card itself takes them back, so the CTA stays clickable.
 */
export const LicenseExpiredOverlay: React.FC<LicenseExpiredOverlayProps> = ({
  subject,
  reason,
  endDate
}) => {
  const navigate = useNavigate();

  return (
    <div
      className="absolute inset-0 z-50 flex items-center justify-center bg-semantic-bg-primary/40 p-6"
      role="status"
      aria-live="polite"
    >
      <div className="pointer-events-auto max-w-md rounded-sm border border-semantic-border-secondary bg-semantic-bg-primary p-6 text-center shadow-sm">
        <ShieldOff className="mx-auto mb-3 h-8 w-8 text-semantic-text-secondary" aria-hidden />
        <h3 className="mb-2 text-base font-semibold text-semantic-text-primary">
          {buildLicenseGateTitle(reason)}
        </h3>
        <p className="text-sm leading-relaxed text-semantic-text-secondary">
          {buildLicenseExpiredBody(subject, reason, endDate)}
        </p>
        <GenericButton
          variant="secondary"
          size="sm"
          className="mt-4"
          label="Go to Licensing"
          onClick={() => navigate(LICENSING_PATH)}
        />
      </div>
    </div>
  );
};

export default LicenseExpiredOverlay;
